import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { format } from 'app/shared/utils/string.util';
import { azureCloudTypeMetadata, azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { Metamodel } from 'app/model/base/model';
import { TrinoCopySource } from 'app/model/pipeline/copysource/trino.copysource';
import { CredentialTypeOptions } from 'shell-models-lib';
import { trinoSHIRVersion } from 'app/shared/utils/version.check.util';

export const trinoMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.trino.linkedServiceType,
    displayName: stringResources.Trino_FormalName,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-Trino'
    },
    copySourceTypes: [Metamodel.modelFor(TrinoCopySource).name],
    copySinkTypes: [],
    isOnPremises: false,
    supportIRVersion: trinoSHIRVersion,
    isFirstClass: true,
    category: ['Database'],
    supportScriptActivity: true,
    supportFirstClassParameterExperience: true,
    supportDatasetV2: true,
    controls: [
        {
            type: 'radio-switch',
            nested: 1,
            label: stringResources.AccountSelectionMethodLabel,
            description: stringResources.TrinoAccountSelectionMethod,
            options: azureResourceSelectMethodOptions,
            defaultValue: '{host != null || parameterized ? "manual" : "azure"}',
            isLabelVertical: true,
            cases: [
                {
                    caseValue: 'azure',
                    children: [
                        {
                            type: 'trino-select',
                            bind: {
                                cluster: 'host'
                            },
                            validators: {
                                cluster: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        }
                    ]
                },
                {
                    caseValue: 'manual',
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'host',
                                label: stringResources.ModelName_Host,
                                required: true,
                                placeholder: 'Example: cluster.clusterPool.subscriptionID.region.hdinsightaks.net',
                                validators: [
                                    { type: 'required', errorMessage: '' },
                                    {
                                        type: 'pattern',
                                        pattern: '^([a-zA-Z0-9-]{1,63}\\.){4,4}(tip\\.)?hdinsightaks(-test)?\\.net$',
                                        errorMessage: format(stringResources.Validator_ErrorMessage_Pattern, '<cluster>.<clusterpool>.<subscriptionID>.<region>.hdinsightaks.net'),
                                    }
                                ],
                                isLabelVertical: true
                            }
                        }
                    ]
                }
            ]
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'catalog',
                label: stringResources.ModelName_Catalog,
                required: true,
                defaultValue: 'system',
                validators: [
                    { type: 'required', errorMessage: '' },
                    { type: 'pattern', errorMessage: format(stringResources.Validator_NameMaxLength, stringResources.Trino_FormalName, '256'), pattern: '^.{1,256}$' },
                    { type: 'pattern', errorMessage: format(stringResources.Validator_NamePattern_All_AlphanumericUnderscore, stringResources.Trino_FormalName), pattern: '^[a-zA-Z0-9_]+$' }
                ],
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'schema',
                required: true,
                label: stringResources.Schema,
                defaultValue: 'information_schema',
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' },
                    { type: 'pattern', errorMessage: format(stringResources.Validator_NameMaxLength, stringResources.Trino_FormalName, '256'), pattern: '^.{0,256}$' },
                    { type: 'pattern', errorMessage: format(stringResources.Validator_NamePattern_All_AlphanumericUnderscore, stringResources.Trino_FormalName), pattern: '^[a-zA-Z0-9_]*$' }
                ]
            }
        },
        {
            type: 'select-switch',
            bind: 'authenticationType',
            description: stringResources.Trino_CredentialWarning,
            label: stringResources.ModelName_AuthenticationType,
            options: [
                {
                    label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
                    value: CredentialTypeOptions.UserAssignedManagedIdentity
                },
                {
                    label: stringResources.AuthenticationType_ManagedServiceIdentity,
                    value: CredentialTypeOptions.SystemAssignedManagedIdentity
                },
                {
                    label: stringResources.AuthenticationType_ServicePrincipal,
                    value: CredentialTypeOptions.ServicePrincipal
                }
            ],
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: CredentialTypeOptions.UserAssignedManagedIdentity,
            cases: [
                {
                    caseValue: CredentialTypeOptions.SystemAssignedManagedIdentity,
                    children: [
                        {
                            type: 'management-identity-info',
                            resourceType: stringResources.Trino_FormalName,
                            // TODO
                            link: 'https://docs.microsoft.com/azure/data-factory'
                        }
                    ]
                },
                {
                    caseValue: CredentialTypeOptions.ServicePrincipal,
                    children: [
                        {

                            type: 'parameterizable',
                            child: {
                                type: 'tenant-textbox',
                                bind: 'tenant',
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true,
                                label: stringResources.ModelName_Tenant,
                                required: true
                            }
                        },
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'clientId',
                                label: stringResources.ModelName_ServicePrincipalID,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true,
                                escapeDescription: false
                            }
                        },
                        {

                            type: 'dynamic-akv-switcher',
                            bind: 'clientSecret',
                            label: stringResources.ModelName_ServicePrincipalKey,
                            enableAKVParameterization: true,
                            child: {
                                type: 'parameterizable',
                                attribute: 'clientSecret',
                                child: {
                                    type: 'password',
                                    label: stringResources.ModelName_ServicePrincipalKey,
                                    required: true,
                                    isLabelVertical: true,
                                    validators: [
                                        { type: 'required', errorMessage: '' }
                                    ]
                                },
                                disableAddParameter: true
                            },
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Trino_FormalName),
                                stringResources.ModelName_ServicePrincipalKey)
                        },
                        azureCloudTypeMetadata
                    ]
                },
                {
                    caseValue: CredentialTypeOptions.UserAssignedManagedIdentity,
                    children: [
                        {
                            type: 'credential-select',
                            bind: 'credential',
                            filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    ]
                }
            ]
        }, // auth
        {
            type: 'key-val-list',
            bind: 'sessionProperties',
            label: stringResources.SessionProperties,
            keyPlaceholder: format(stringResources.Example, 'join_distribution_type'),
            valPlaceholder: format(stringResources.Example, 'AUTOMATIC')
        }
    ]
};
